import type { Color } from "@salestrak/ui";

/**
 * How an outcome reads on the entity — positive / neutral / negative. The tone
 * DESCRIBES the interaction so a feed or a settings list scans at a glance; it
 * never acts. No close, no automation, no gate: the ACT carries the
 * consequence, the word only names how it went.
 *
 * Client-local by construction — the follow-up outcome pool it describes is
 * prototype data, so this set has no schema enum to import. Written as a
 * frozen tuple + guard so it narrows the same way `lib/data/entities/sets.ts`
 * narrows the server's closed sets.
 */
export type OutcomeTone = "positive" | "neutral" | "negative";

export const OUTCOME_TONES = ["positive", "neutral", "negative"] as const;

export function isOutcomeTone(value: unknown): value is OutcomeTone {
  return (
    typeof value === "string" &&
    (OUTCOME_TONES as readonly string[]).includes(value)
  );
}

/** Label + dot color per tone. Positive/negative echo the age-status ladder's
 *  on-track/lapsed reading (green/red); neutral is deliberately colorless
 *  grey — most outcomes are neutral, and a page of colored dots says nothing. */
export const OUTCOME_TONE_CONFIG: Record<
  OutcomeTone,
  { label: string; swatch: Color }
> = {
  positive: { label: "Positive", swatch: "green" },
  neutral: { label: "Neutral", swatch: "grey" },
  negative: { label: "Negative", swatch: "red" },
};

/** The launch vocabulary — good/bad, before the rename to positive/negative.
 *  A tone stored under a retired word (server `reason` rows predating the
 *  ReasonTones migration, snapshots on old activity) maps here on read. */
const LEGACY_OUTCOME_TONES: Record<string, OutcomeTone> = {
  good: "positive",
  bad: "negative",
};

/** Absent, pre-tone-era, or corrupt tone reads neutral — the default an
 *  outcome created without one carries. Retired words keep their meaning. */
export function normalizeOutcomeTone(
  value: string | null | undefined,
): OutcomeTone {
  if (isOutcomeTone(value)) return value;
  return LEGACY_OUTCOME_TONES[value ?? ""] ?? "neutral";
}
